import { zodResolver } from '@hookform/resolvers/zod';
import { useDisclosure } from '@nextui-org/react';
import { useContext, useState } from 'react';
import {
  Control,
  FieldErrors,
  useForm,
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormSetValue,
} from 'react-hook-form';
import toast from 'react-hot-toast';
import { z } from 'zod';

import {
  Theme,
  ThemeContext,
} from '../../../contexts/ThemeContext/ThemeContext';
import { ICubage } from '../../../interfaces/response/Cubage/ICubage';
import {
  AddCubagens,
  EditCubagens,
} from '../../../redux/features/cubages/cubagesThunk';
import { useAsyncDispatch } from '../../../redux/store';

const formSchema = z.object({
  ean: z.string().min(1, 'EAN é obrigatório').length(13, 'EAN inválido'),
  descricao: z.string().min(1, 'Descrição é obrigatória'),
  altura: z.string().min(1, 'Altura é obrigatória'),
  largura: z.string().min(1, 'Largura é obrigatória'),
  profundidade: z.string().min(1, 'Profundidade é obrigatória'),
});

type FormSchema = z.infer<typeof formSchema>;

interface UseRegisterCubageModalProps {
  onUpdate?: () => void;
}

export interface UseRegisterCubageModal {
  errors: FieldErrors<FormSchema>;
  handleSubmitCubage: (data: FormSchema) => Promise<void>;
  handleClose: () => void;
  handleSubmit: UseFormHandleSubmit<FormSchema>;
  handleOpenEdit: (CubageAux: ICubage) => void;
  isLoading: boolean;
  isOpen: boolean;
  isValid: boolean;
  onOpen: () => void;
  onOpenChange: () => void;
  register: UseFormRegister<FormSchema>;
  theme: Theme;
  control: Control<FormSchema>;
  setValue: UseFormSetValue<FormSchema>;
  values: FormSchema;
  cubage: ICubage | null;
}

const useRegisterCubageModal = ({
  onUpdate,
}: UseRegisterCubageModalProps): UseRegisterCubageModal => {
  const dispatch = useAsyncDispatch();
  const { isOpen, onOpen, onClose, onOpenChange } = useDisclosure();
  const { theme } = useContext(ThemeContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [cubage, setCubage] = useState<ICubage | null>(null);
  const defaultValues: FormSchema = {
    ean: '',
    descricao: '',
    altura: '0,00',
    largura: '0,00',
    profundidade: '0,00',
  };
  const {
    register,
    handleSubmit,
    clearErrors,
    formState,
    reset,
    control,
    setValue,
    watch,
  } = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues,
    mode: 'onTouched',
    reValidateMode: 'onChange',
    resetOptions: {
      keepDefaultValues: true,
    },
  });
  const { errors, isValid } = formState;
  const values = watch();

  const handleClose = (): void => {
    onClose();
    setCubage(null);
    clearErrors();
    reset();
  };

  const handleCreateCubage = async (data: FormSchema): Promise<void> => {
    const { ean, descricao, largura, altura, profundidade } = data;

    const body: ICubage = {
      idCubagem: 0,
      descricao: descricao.toUpperCase(),
      ean: ean.toUpperCase(),
      altura: Number(altura.replaceAll('.', '').replace(',', '.')),
      largura: Number(largura.replaceAll('.', '').replace(',', '.')),
      profundidade: Number(profundidade.replaceAll('.', '').replace(',', '.')),
    };

    setIsLoading(true);
    await dispatch(
      AddCubagens({
        body,
      })
    )
      .unwrap()
      .then(() => {
        toast.success('Cubagem cadastrada com sucesso!');
        handleClose();
        if (onUpdate) onUpdate();
      })
      .catch(() => {
        toast.error(
          'Ocorreu um erro ao realizar o cadastro da cubagem, por favor tente mais tarde.'
        );
      })
      .finally(() => setIsLoading(false));
  };

  const handleEditCubage = async (data: FormSchema): Promise<void> => {
    const { ean, descricao, largura, altura, profundidade } = data;

    if (cubage) {
      const body: ICubage = {
        idCubagem: cubage.idCubagem,
        descricao: descricao.toUpperCase(),
        ean: ean.toUpperCase(),
        altura: Number(altura.replaceAll('.', '').replace(',', '.')),
        largura: Number(largura.replaceAll('.', '').replace(',', '.')),
        profundidade: Number(
          profundidade.replaceAll('.', '').replace(',', '.')
        ),
      };
      setIsLoading(true);
      await dispatch(
        EditCubagens({
          body,
        })
      )
        .unwrap()
        .then(() => {
          toast.success('Cubagem editada com sucesso!');
          handleClose();
          if (onUpdate) onUpdate();
        })
        .catch(() => {
          toast.error(
            'Ocorreu um erro ao realizar a alteração da cubagem, por favor tente mais tarde.'
          );
        })
        .finally(() => setIsLoading(false));
    }
  };

  const handleSubmitCubage = async (data: FormSchema): Promise<void> => {
    if (cubage) handleEditCubage(data);
    else handleCreateCubage(data);
  };

  const handleOpenEdit = (CubageAux: ICubage): void => {
    setCubage(CubageAux);
    setValue('ean', CubageAux?.ean.toUpperCase() ?? '');
    setValue('descricao', CubageAux?.descricao.toUpperCase() ?? '');
    setValue(
      'altura',
      Number(CubageAux?.altura).toFixed(2).replace('.', ',') ?? '0,00'
    );
    setValue(
      'largura',
      Number(CubageAux?.largura).toFixed(2).replace('.', ',') ?? '0,00'
    );
    setValue(
      'profundidade',
      Number(CubageAux?.profundidade).toFixed(2).replace('.', ',') ?? '0,00'
    );
    onOpen();
  };

  return {
    errors,
    handleSubmitCubage,
    handleClose,
    handleSubmit,
    handleOpenEdit,
    isLoading,
    isOpen,
    isValid,
    onOpen,
    onOpenChange,
    register,
    theme,
    control,
    setValue,
    values,
    cubage,
  };
};

export default useRegisterCubageModal;
